/**
 * Label, control and message as one field.
 *
 * RNR ships those as three components; composing them is the design decision, so the field is what
 * the system names. The invalid state is carried on the field rather than on the input, so the
 * border and the message colour together — one red border with a neutral message underneath reads
 * as decoration.
 */
import { Input } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import { useId } from 'react';
import { View, type TextInputProps } from 'react-native';

export function Field({
  label,
  message,
  invalid,
  className,
  ...input
}: TextInputProps & {
  label: string;
  /** The description, or the reason it is invalid. The two share a line because they never coexist. */
  message?: string;
  invalid?: boolean;
  className?: string;
}) {
  const id = useId();
  return (
    <View className={cn('gap-1', className)}>
      {/**
       * The label is the system's `label` variant rather than RNR's `Label`, and that is a type
       * fix as much as a treatment: `Label` draws its text through the primitive, which never
       * carries the body face on the base — so on the web it fell through to the browser stack,
       * next to an input that is set in Figtree. `nativeID` keeps the `aria-labelledby` pairing.
       */}
      <Text variant="label" nativeID={id}>
        {label}
      </Text>
      {/* The restyle's field chrome: the card's own surface under the card's own hairline, so a
          control reads as cut into the sheet rather than laid on it. */}
      <Input
        aria-labelledby={id}
        aria-invalid={invalid}
        className={cn('border-border bg-card', invalid && 'border-destructive')}
        {...input}
      />
      {message ? (
        <Text variant="small" className={cn(invalid ? 'text-destructive' : 'text-muted-foreground')}>
          {message}
        </Text>
      ) : null}
    </View>
  );
}
